// Which overlay line (HiiKER hikes, my trails, hike lists, cycling routes) a
// point on the map lands on, and what opening it shows. Shared by the overlay
// click handler (BaseMapDataOverlaysModule.initMapClickHandler) and the route
// planner's right-click menu, which offers the same thing as "View <name>".
import {
  HIIKER_TRAILS_MAIN_ID, HIIKER_TRAILS_BORDER_ID, HIIKER_TRAILS_SOURCE_ID,
  CYCLING_NCN_ID, CYCLING_LINK_ID,
} from './BaseMapDialogHelpers';
import { HIKE_LIST_OVERLAY_SOURCE_ID } from '../../../utils/hikeListOverlay';

/** How far (px) from a line a click still lands on it. */
export const OVERLAY_HIT_RADIUS = 6;

export interface OverlayClickState {
  activeSources: string[];
  officialHikesActive: boolean;
  hikeListsActive: boolean;
  cyclingRoutesActive: boolean;
}

type Properties = Record<string, any>;

/** A cycling route opens its inline popup; every other overlay line opens the trail card for its source. */
export type OverlayHit =
  | { kind: 'cycling'; properties: Properties; name: string | null }
  | { kind: 'card'; source: string; properties: Properties; name: string | null };

interface RenderedFeature {
  layer?: { id?: string };
  source?: string;
  properties?: Properties | null;
}

interface QueryableMap {
  getLayer(id: string): unknown;
  queryRenderedFeatures(bbox: [[number, number], [number, number]], options: { layers: string[] }): RenderedFeature[];
}

/** The overlay layers a click can open, given which overlays are switched on. */
export function overlayClickableLayers(state: OverlayClickState): string[] {
  const layers = [...state.activeSources]; // each "my trails" layer is named after its source
  if (state.officialHikesActive) layers.push(HIIKER_TRAILS_MAIN_ID);
  if (state.hikeListsActive) layers.push(HIKE_LIST_OVERLAY_SOURCE_ID);
  if (state.cyclingRoutesActive) layers.push(CYCLING_NCN_ID, CYCLING_LINK_ID);
  return layers;
}

/** The name the line is labelled with on the map: HiiKER hikes carry `name`, hike-list and my-trails lines `title`. */
export function overlayFeatureName(properties: Properties | null | undefined): string | null {
  const name = properties?.name ?? properties?.title;
  return typeof name === 'string' && name.trim() ? name.trim() : null;
}

/** What opening a rendered overlay feature shows, or null when it can't be opened. */
export function overlayHitFromFeature(feature: RenderedFeature): OverlayHit | null {
  const layerId = feature.layer?.id;
  if (layerId === CYCLING_NCN_ID || layerId === CYCLING_LINK_ID) {
    return { kind: 'cycling', properties: feature.properties || {}, name: null };
  }

  // The HiiKER hikes layers draw one source; my-trails / hike-list layers share their source's id.
  const source = layerId === HIIKER_TRAILS_MAIN_ID || layerId === HIIKER_TRAILS_BORDER_ID
    ? HIIKER_TRAILS_SOURCE_ID
    : feature.source;
  if (!feature.properties || !source) return null;

  return { kind: 'card', source, properties: feature.properties, name: overlayFeatureName(feature.properties) };
}

/**
 * The overlay layers to query at a click — those switched on AND on the map
 * now. State can say a layer is on just before it's added or after it's gone.
 */
export function queryableOverlayLayers(map: QueryableMap, state: OverlayClickState): string[] {
  return overlayClickableLayers(state).filter((id) => map.getLayer(id));
}

/** The rendered overlay features within reach of a point on the map, nearest-drawn first. */
export function overlayFeaturesAt(
  map: QueryableMap, point: { x: number; y: number }, layers: string[],
): RenderedFeature[] {
  if (layers.length === 0) return [];
  const r = OVERLAY_HIT_RADIUS;
  return map.queryRenderedFeatures([[point.x - r, point.y - r], [point.x + r, point.y + r]], { layers });
}

/** The overlay line within reach of a point that can be opened, or null. */
export function overlayHitAt(
  map: QueryableMap | null | undefined, point: { x: number; y: number } | null | undefined,
  state: OverlayClickState | null | undefined,
): OverlayHit | null {
  if (!map || !point || !state) return null;
  const [feature] = overlayFeaturesAt(map, point, queryableOverlayLayers(map, state));
  return feature ? overlayHitFromFeature(feature) : null;
}

/**
 * The route planner owns left clicks on its map — each one adds or edits a
 * route point — so an overlay line under the click mustn't also open its
 * trail card and highlight on top. People tracing a HiiKER hike click right on
 * its line. The planner's right-click menu opens the line instead ("View …").
 * Only the planner's store registers a `planTrail` module.
 */
export function plannerOwnsMapClick(rootState: { planTrail?: unknown } | null | undefined): boolean {
  return !!rootState?.planTrail;
}
